import {
  FlowArrow,
  MagnifyingGlass,
  SlidersHorizontal,
  Sparkle,
} from '@phosphor-icons/react';
import { Button, EmptyState, toast } from '@reelplicate/ui';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { AssetDrawer } from './asset-drawer';
import { BulkBar } from './bulk-bar';
import { FilterBar, type View } from './filter-bar';
import { applyFilters, type FilterState, search, sortHits, type SortId } from './library';
import { useMedia } from './media-context';
import { useMediaOutlet } from './media-outlet';
import { MediaTable } from './media-table';
import { MediaTile } from './media-tile';
import type { Asset, SmartRule } from './types';

type AssetBrowserProps = {
  /** The assets in scope (the whole library, a type, or a collection). */
  assets: Asset[];
  /** The AI search query, if any. */
  query?: string;
  /** Shown between the filters and the results (e.g. a collection tip). */
  notice?: ReactNode;
  /** Replaces the empty state when there's nothing in scope at all. */
  empty?: ReactNode;
};

/**
 * Filters, sort and view over a set of assets, as a grid or a list, with
 * multi-select (bulk bar) and the asset drawer. Shared by the library and
 * collection pages.
 */
export function AssetBrowser({ assets, query = '', notice, empty }: AssetBrowserProps) {
  const { loading, collections, facets } = useMedia();
  const { editSmartCollection } = useMediaOutlet();
  const [filters, setFilters] = useState<FilterState>({});
  const [sort, setSort] = useState<SortId>('newest');
  const [view, setView] = useState<View>('grid');
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [openId, setOpenId] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const linked = params.get('asset');

  const searching = query.trim().length > 0;
  // A new search sorts by relevance until the user picks another order.
  const [sortedFor, setSortedFor] = useState('');
  const effectiveSort: SortId =
    searching && sortedFor !== query
      ? 'relevance'
      : !searching && sort === 'relevance'
        ? 'newest'
        : sort;

  const hits = useMemo(
    () => sortHits(search(applyFilters(assets, filters, facets), query), effectiveSort),
    [assets, filters, facets, query, effectiveSort],
  );
  const visible = hits.map((h) => h.asset);
  const selectedIds = [...selected].filter((id) => assets.some((a) => a.id === id));

  // What a smart collection made from here keeps matching.
  const smartRule: SmartRule = { filters, query: searching ? query.trim() : undefined };

  // `?asset=<id>` (e.g. from the command palette) opens that asset's drawer
  // once the library has loaded, then leaves the URL.
  if (linked && !loading && linked !== openId && assets.some((a) => a.id === linked)) {
    setOpenId(linked);
  }
  useEffect(() => {
    if (!linked || loading) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('asset');
        return next;
      },
      { replace: true },
    );
  }, [linked, loading, setParams]);

  const index = visible.findIndex((a) => a.id === openId);
  // An asset opened by link can be hidden by the filters: still show it.
  const open =
    index >= 0 ? visible[index]! : (assets.find((a) => a.id === openId) ?? null);

  const toggle = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  return (
    <>
      <FilterBar
        filters={filters}
        onFiltersChange={setFilters}
        sort={effectiveSort}
        onSortChange={(next) => {
          setSort(next);
          setSortedFor(query);
        }}
        searching={searching}
        view={view}
        onViewChange={setView}
      />

      {searching && (
        <div className="bg-brand-subtle flex flex-wrap items-center gap-3 rounded-lg py-3 pr-3 pl-4">
          <Sparkle aria-hidden className="text-icon-brand size-[18px]" />
          <div className="flex min-w-0 flex-1 flex-col">
            <p role="status" className="text-label-md text-text-primary">
              {hits.length} {hits.length === 1 ? 'result' : 'results'} for “{query.trim()}
              ”
            </p>
            <p className="text-caption text-text-secondary">
              Matched on tags, titles, captions and transcripts ·{' '}
              {effectiveSort === 'relevance'
                ? 'sorted by relevance'
                : 'sorted by your choice'}
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            leadingIcon={FlowArrow}
            onClick={() => toast('Flows are coming soon')}
          >
            Use results in a flow
          </Button>
          <Button
            size="sm"
            leadingIcon={SlidersHorizontal}
            onClick={() =>
              editSmartCollection({
                initial: {
                  name: query
                    .trim()
                    .replace(/^\w/, (c) => c.toUpperCase())
                    .slice(0, 60),
                  rule: smartRule,
                },
              })
            }
          >
            Save as smart collection
          </Button>
        </div>
      )}

      {notice}

      {loading ? null : assets.length === 0 && empty ? (
        empty
      ) : visible.length === 0 ? (
        <EmptyState
          icon={MagnifyingGlass}
          title={searching ? `Nothing matches “${query.trim()}”` : 'Nothing here yet'}
          description={
            searching || Object.values(filters).some((v) => v.length)
              ? 'Try fewer filters or other words.'
              : 'Upload files or generate reels with Flows to fill it.'
          }
          className="mx-auto"
        />
      ) : view === 'grid' ? (
        <ul
          aria-label="Media"
          className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-x-[25px] gap-y-6"
        >
          {hits.map(({ asset, reason }) => (
            <li key={asset.id}>
              <MediaTile
                asset={asset}
                reason={reason}
                selected={selected.has(asset.id)}
                selecting={selectedIds.length > 0}
                onSelectedChange={(on) => toggle(asset.id, on)}
                onOpen={() => setOpenId(asset.id)}
              />
            </li>
          ))}
        </ul>
      ) : (
        <MediaTable
          assets={visible}
          collections={collections}
          selected={selected}
          onSelectedChange={setSelected}
          onOpen={(asset) => setOpenId(asset.id)}
        />
      )}

      {selectedIds.length > 0 && (
        <BulkBar
          selectedIds={selectedIds}
          onClear={() => setSelected(new Set())}
          smartRule={smartRule}
        />
      )}

      <AssetDrawer
        asset={open}
        query={query}
        onClose={() => setOpenId(null)}
        onPrevious={index > 0 ? () => setOpenId(visible[index - 1]!.id) : undefined}
        onNext={
          index >= 0 && index < visible.length - 1
            ? () => setOpenId(visible[index + 1]!.id)
            : undefined
        }
      />
    </>
  );
}
